import {
  ModelKazaiOptionServices,
  ModelYhcKazaiDeliveryPeakSeasonRules,
  ModelYhcKazaiKaguidoDestinationOptionsAndPrices,
  ModelYhcKazaiKaguidoDestinationQuestions,
  ModelYhcKazaiTypesFullRelation,
  ModelYhcKazaiTypesSubCategories,
  ModelYhcOptions,
} from '../../fireModels/yhcDataTypes'
import { $core } from '../../../CORE/src/front/$core'
import { YhcEFInputOneKazaiTypeDataObject } from './YhcEFInputOneKazaiTypeDataObject'
import { YhcEFUserInputDataHandler } from './YhcEFUserInputDataHandler'
import { reactive, computed } from 'vue'
import { findRecordsByModelName } from './findRecordsByModelName'
import {
  fetchMasterVersionPeriods,
  type YhcMasterVersionPeriod,
} from './masterVersionByDate'

/**
 * 見積りフォーム全体を司るサービス
 *
 * ## 責任範囲
 * - 家財マスタデータの取得, 初期化
 *   - 家財の種類, オプションサービス
 * - 家具移動オプションマスタ
 * - 繁忙期マスタの取得と保持
 * - ユーザの入力データを保持する
 * - バージョン情報を 保持する / 切り替え実施 (再初期化)
 */
export class YhcEstimateFormFrontService {
  /**
   * 設定: 基準となる日時
   * - 通常は 現在日時
   * - 管理画面プレビューでは、日時を指定可能
   */
  baseDatetime: string = ''
  /**
   * 設定: ユーザの入力データ local storage で 保存 (& 復元) するかどうか
   */
  enableAutoSaveOfUserInputData: boolean = false
  /**
   * 設定: デバッグモード
   * - 管理画面プレビューでのみ true に設定可能
   */
  isDebugMode: boolean = false
  /**
   * フリマ用ページかどうか
   */
  isFleaMarket: boolean = false

  /**
   * サービスレベル検索ページかどうか
   */
  isServiceLevel: boolean = false

  /**
   * 法人向けページかどうか（料金非表示・繁忙期ルール非適用）
   */
  isCorporationView: boolean = false

  /**
   * 法人向け・リードタイム+1日ページかどうか（サービスレベル検索専用）
   */
  isCorporationLeadTimePlusOne: boolean = false

  /**
   * "すべての" 家財タイプを保持する, 有効期限内かどうか関係なく、全て API から fetch, 保持している
   * 初期化時に fetch され、その後変更されない
   * 有効期限内であるものは `get kazaiTypesWithBaseDatetime()` で 算出 & 参照する
   * @private
   */
  private _allKazaiTypes: ModelYhcKazaiTypesFullRelation[] = []
  /**
   * 家財タイプ ID で参照できるように
   */
  public allKazaiTypesById: {
    [id: number]: ModelYhcKazaiTypesFullRelation
  } = {}
  /**
   * オプションサービス ID で参照できるように
   */
  allOptionServicesById: {
    [id: number]: ModelKazaiOptionServices
  } = {}
  /**
   * お預かり日時点のマスタバージョン
   */
  versionPickup: string = ''
  /**
   * お届け日時点のマスタバージョン
   */
  versionDelivery: string = ''
  /**
   * バージョン情報（お預かり日版。互換用）
   */
  get version(): string {
    return this.versionPickup
  }
  /**
   * 全マスタバージョンの有効期間（サービスレベルまたぎ注釈用）
   */
  masterVersionPeriods: YhcMasterVersionPeriod[] = []

  /**
   * ローディング中かどうか
   * - 初期Load時に 一瞬 true になる
   */
  isLoading: boolean = false

  /**
   * ユーザの入力データを保持する Class instance を保持
   */
  $userInputData: YhcEFUserInputDataHandler = null

  /**
   * 家財に紐づかない オプションサービス by key
   */
  generalOptionsByKey: {
    [K in ModelYhcOptions['key']]?: ModelYhcOptions
  } = {}

  /**
   * 家具移動オプション 質問マスタ
   */
  kaguidoOptionQuestions: ModelYhcKazaiKaguidoDestinationQuestions[] = []

  /**
   * 家具移動オプション 選択肢マスタ
   */
  kaguidoOptionSelections: ModelYhcKazaiKaguidoDestinationOptionsAndPrices[] = []

  /**
   * 繁忙期シーズンルールマスタ
   */
  deliveryPeakSeasonRules: ModelYhcKazaiDeliveryPeakSeasonRules[] = []

  private _subCategories: ModelYhcKazaiTypesSubCategories[] = []
  private _subCategoriesByParentCategory: {
    [categoryName: string]: ModelYhcKazaiTypesSubCategories[]
  } = {}

  constructor() {
    this.baseDatetime = $core.$dayjs().format('YYYY-MM-DD HH:mm:ss')
  }

  private updateSubCategories() {
    const catById = this.kazaiTypesWithBaseDatetime.reduce(
      (acc, kazaiType) => {
        acc[kazaiType.subCategory.id] = kazaiType.subCategory
        return acc
      },
      {} as { [id: number]: ModelYhcKazaiTypesSubCategories },
    )

    this._subCategories = Object.values(catById).sort((a, b) => a.sort - b.sort)

    this._subCategoriesByParentCategory = this._subCategories.reduce(
      (acc, subCategory) => {
        if (!acc[subCategory.category]) {
          acc[subCategory.category] = []
        }
        acc[subCategory.category].push(subCategory)
        return acc
      },
      {} as { [categoryName: string]: ModelYhcKazaiTypesSubCategories[] },
    )
  }

  get subCategories(): ModelYhcKazaiTypesSubCategories[] {
    return this._subCategories
  }

  get subCategoriesByParentCategory(): {
    [categoryName: string]: ModelYhcKazaiTypesSubCategories[]
  } {
    return this._subCategoriesByParentCategory
  }

  /**
   * 初期化
   *
   * 1. マスタデータの取得
   */
  async init() {
    this.isLoading = true
    await Promise.all([
      this._fetchKazaiMaster(),
      this._fetchMasterVersionPeriods(),
      this._fetchOptions(),
      this._fetchKaguidoOptions(),
      this._fetchDeliveryPeakSeasonRules(),
    ])
    this.initUserInputData()
    await this.$userInputData.fetchFixedHolidays()
    await this.$userInputData.adjustInitialPickupDate()
    await this.$userInputData.refreshMastersForSelectedDates()
    this.updateSubCategories()
    this.isLoading = false
  }

  private initUserInputData() {
    this.$userInputData = reactive(
      new YhcEFUserInputDataHandler(this),
    ) as YhcEFUserInputDataHandler
  }

  /**
   * 指定された baseDatetime に基づいて、利用する家財種別をFilterする
   *
   * - 基本的には enabledFrom <= baseDatetime <= disabledAt のものを利用する
   * - debugMode が true の場合は isEnabledOnDebug が true のものも利用する
   *
   */
  get kazaiTypesWithBaseDatetime(): ModelYhcKazaiTypesFullRelation[] {
    return filterKazaiTypesWithBaseDatetime(
      this._allKazaiTypes,
      this.baseDatetime,
      this.isDebugMode,
    )
  }

  async _fetchKazaiMaster() {
    // this._allKazaiTypes = await $core.$lsCache.getWithFetchFunc('yhc_kazai_types', () => fetchAllKazaiTypesWithRelations(), 60 * 60 * 24 * 7)
    this._allKazaiTypes = await fetchAllKazaiTypesWithRelations()
    this.allKazaiTypesById = this._allKazaiTypes.reduce(
      (acc, kazaiType) => {
        acc[kazaiType.id] = kazaiType
        // allOptionServicesById
        kazaiType.serviceOptions.map((serviceOption) => {
          this.allOptionServicesById[serviceOption.service.id] = serviceOption.service
        })
        return acc
      },
      {} as { [id: number]: ModelYhcKazaiTypesFullRelation },
    )
  }

  async _fetchMasterVersionPeriods() {
    this.masterVersionPeriods = await fetchMasterVersionPeriods()
  }
  async _fetchOptions() {
    this.generalOptionsByKey = (
      await fetchOptionsBasedOnDatetime(this.baseDatetime)
    ).reduce(
      (acc, option) => {
        acc[option.key] = option
        return acc
      },
      {} as { [K in ModelYhcOptions['key']]?: ModelYhcOptions },
    )
  }
  async _fetchKaguidoOptions() {
    this.kaguidoOptionQuestions = await fetchAllKaguidoOptionQuestionsBasedOnDatetime(
      this.baseDatetime,
    )
    this.kaguidoOptionSelections = await fetchAllKaguidoOptionSelectionsBasedOnDatetime(
      this.baseDatetime,
    )
  }

  /**
   * 終了日が現在日以降となっている繁忙期シーズンルールマスタを全て取得する
   */
  private async _fetchDeliveryPeakSeasonRules() {
    this.deliveryPeakSeasonRules = await findRecordsByModelName(
      'yhc_kazai_delivery_peak_season_rules',
      {
        filter: {
          endDate: { _gte: this.baseDatetime },
        },
        sort: ['startDate'],
        limit: -1,
      },
    )
  }

  // この方法を呼び出して、kazaiTypesWithBaseDatetimeが変更されたときにサブカテゴリを更新します
  updateKazaiTypes() {
    // ... existing logic to update kazaiTypesWithBaseDatetime ...
    this.updateSubCategories()
  }
}

export const fetchAllKazaiTypesWithRelations = async (): Promise<
  ModelYhcKazaiTypesFullRelation[]
> => {
  return findRecordsByModelName('yhc_kazai_types', {
    fields: ['*', 'serviceOptions.*', 'serviceOptions.service.*', 'subCategory.*'],
    sort: ['sort'],
    limit: -1,
  })
}

export { fetchVersionBasedOnDatetime } from './masterVersionByDate'

/**
 * 指定された日時に基づいて、(家財に紐づかない) options を取得する
 * @param baseDatetime
 */
export const fetchOptionsBasedOnDatetime = async (
  baseDatetime: string,
): Promise<ModelYhcOptions[]> => {
  return findRecordsByModelName('yhc_options', {
    fields: ['*'],
    filter: {
      enabledFrom: { _lte: baseDatetime },
      disabledAt: { _gte: baseDatetime },
    },
  })
}

/**
 * 指定された日時に基づいて、家具移動オプション質問マスタを取得する
 */
export const fetchAllKaguidoOptionQuestionsBasedOnDatetime = async (
  baseDatetime: string,
): Promise<ModelYhcKazaiKaguidoDestinationQuestions[]> => {
  return findRecordsByModelName('yhc_kazai_kaguido_destination_questions', {
    fields: ['*'],
    filter: {
      enabledFrom: { _lte: baseDatetime },
      disabledAt: { _gte: baseDatetime },
    },
    sort: ['sort'],
  })
}

/**
 * 指定された日時に基づいて、家具移動オプション選択肢マスタを取得する
 */
export const fetchAllKaguidoOptionSelectionsBasedOnDatetime = async (
  baseDatetime: string,
): Promise<ModelYhcKazaiKaguidoDestinationOptionsAndPrices[]> => {
  return findRecordsByModelName('yhc_kazai_kaguido_destination_options_and_prices', {
    fields: ['*.*'],
    filter: {
      enabledFrom: { _lte: baseDatetime },
      disabledAt: { _gte: baseDatetime },
    },
  })
}

/**
 * 指定された日時に基づいて、利用可能な家財種別をFilterする
 * @param allKazaiTypes
 * @param baseDatetime
 * @param isDebugMode
 */
const filterKazaiTypesWithBaseDatetime = (
  allKazaiTypes: ModelYhcKazaiTypesFullRelation[],
  baseDatetime: string,
  isDebugMode: boolean = false,
) => {
  return allKazaiTypes.reduce((acc, kazaiType) => {
    if (
      (kazaiType.enabledFrom <= baseDatetime && baseDatetime <= kazaiType.disabledAt) ||
      (isDebugMode && kazaiType.isEnabledOnDebug)
    ) {
      // サービスオプションについても 指定 datetime で filter する
      kazaiType.serviceOptions =
        kazaiType.serviceOptions?.reduce(
          (serviceOptions, serviceOption) => {
            if (
              (serviceOption.service.enabledFrom <= baseDatetime &&
                baseDatetime <= serviceOption.service.disabledAt) ||
              (isDebugMode && serviceOption.service.isEnabledOnDebug)
            ) {
              // サービスオプション 内の 選択肢についても 有効期限内の selections のみ filter する
              const validSelections = serviceOption.service?.selections?.filter(
                (selection) => {
                  return (
                    selection.enabledFrom <= baseDatetime &&
                    baseDatetime <= selection.disabledAt
                  ) // || (this.isDebugMode && selection.isEnabledOnDebug)
                },
              )
              if (validSelections?.length) {
                serviceOptions.push({
                  ...serviceOption,
                  service: {
                    ...serviceOption.service,
                    selections: validSelections,
                  },
                })
              }
            }
            return serviceOptions
          },
          [] as ModelYhcKazaiTypesFullRelation['serviceOptions'],
        ) || []
      acc.push(kazaiType)
    }
    return acc
  }, [] as ModelYhcKazaiTypesFullRelation[])
}
